import { useMemo, useState } from "react";

import clsx from "clsx";

import { TitleSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { NBX_SUPPORTED_METRICS } from "@features/metrics-explorer/constants";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import {
  type BenchmarkData,
  type MetricData,
  type NorthbeamIndexData,
} from "@features/metrics-explorer/types";
import {
  type BenchmarkIndicatorRow,
  UniversalBenchmarksIndicator,
} from "@features/universal-benchmarks";
import { useFeatureFlags } from "@hooks/http";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { type MetricsExplorerCorrelationRow } from "../metrics/correlation-utils";
import { TileModal } from "../tile-modal";

import { CorrelationRow } from "./correlation-row";
import { IndexCheckbox } from "./index-checkbox";
import { OverrideTags } from "./override-tags";

interface Props {
  index: number;
  width: number;
  metricData: MetricData;
  benchmarkData?: BenchmarkData;
  correlation?: MetricsExplorerCorrelationRow;
  onDuplicate(): void;
  onDelete(): void;
}

export const MetricCard = ({
  index,
  width,
  metricData,
  benchmarkData,
  correlation,
  onDuplicate,
  onDelete,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { hasFeatureFlag } = useFeatureFlags();
  const { designElementManager } = useDesignElementManagerV2();
  const { tiles, filters, setTiles, buildMetrics } =
    useMetricsExplorerContext();

  const [isEditing, setIsEditing] = useState(false);
  const [isInternalTooltipVisible, setIsInternalTooltipVisible] =
    useState(false);

  const tile = useMemo(() => {
    return tiles.find((e) => e.id === metricData.tileId);
  }, [metricData, tiles]);

  const metrics = useMemo(() => {
    if (!tile) {
      return [];
    }

    return buildMetrics({
      attributionModel: tile.attributionModel ?? filters.attributionModel,
      attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
      accountingMode: tile.accountingMode ?? filters.accountingMode,
    });
  }, [
    buildMetrics,
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    tile,
    tile?.attributionModel,
    tile?.attributionWindow,
    tile?.accountingMode,
  ]);

  const metric = useMemo(() => {
    return tile && metrics.find((e) => e.id === tile.metricId);
  }, [metricData, tile, metrics]);

  const value = useMemo(() => {
    return metricData.data && metric
      ? metric.getValue(metricData.data.summary.metrics)
      : 0;
  }, [metric, metricData.data]);

  const benchmarkIndicatorRow = useMemo(() => {
    if (!benchmarkData?.benchmark || !metric) {
      return null;
    }

    return {
      name: metric.name,
      current: value,
      target: benchmarkData.benchmark,
      format: metric.format,
      positiveTrend: metric.positiveTrend,
    } as BenchmarkIndicatorRow;
  }, [benchmarkData, value, metric]);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: metricData.tileId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!tile) {
    return null;
  }

  if (!metric) {
    return (
      <div
        className="h-[134px] border bg-v2-primary rounded-lg py-3 px-4 relative"
        style={{ width: isNaN(width) ? 0 : width }}
      >
        <TitleSlide className="text-xs text-center">
          Could not find this Traffic Source.
          <br />
          Try using different accounting mode!
        </TitleSlide>
      </div>
    );
  }

  const isCorrelationDisabled =
    !hasFeatureFlag("enableMetricsExplorerCorrelationAnalysis") &&
    tile.index > 1;

  const labelColor = designElementManager.getColorForIndex(index);

  const name = tile.name ?? metric.name;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx({ "z-[999]": isDragging })}
    >
      <GenericTooltip
        noInfoCircle
        placement="top"
        mouseEnterDelay={500}
        showTooltip={
          !(tile.active || isCorrelationDisabled || isInternalTooltipVisible)
        }
        content="Click to show correlation analysis against this metric"
      >
        <div
          className={clsx(
            "h-[134px] border bg-v2-primary rounded-lg overflow-hidden",
            "flex flex-col justify-between",
            {
              "border-v2-action ring-2 ring-v2-highlight-information":
                tile.active,
              "border-v2-tertiary hover:border-v2-light": !tile.active,
              "cursor-pointer hover:bg-v2-secondary": !isCorrelationDisabled,
              "shadow-sm": isDragging,
            },
          )}
          style={{ width: isNaN(width) ? 0 : width }}
          onClick={() => {
            if (isCorrelationDisabled) {
              return;
            }

            const newTiles = tiles.map((e) =>
              e.id === tile.id
                ? { ...e, active: true }
                : { ...e, active: false },
            );

            prettyLogEventPage({
              domain: "Metrics Explorer",
              action: "onClick",
              additionalActionInfo: "Activate tile",
              data: {
                newTiles,
                pageContext: { tiles },
              },
            });

            setTiles(newTiles);
          }}
        >
          <div className="pt-1.5 px-3 flex-1 flex flex-col">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-1.5 truncate">
                <div
                  ref={setActivatorNodeRef}
                  {...attributes}
                  {...listeners}
                  className={clsx("text-xs", {
                    "cursor-grab": !isDragging,
                    "cursor-grabbing": isDragging,
                  })}
                >
                  <i className="fa-solid fa-grip-dots-vertical text-xs text-v2-secondary opacity-25 hover:opacity-100"></i>
                </div>
                <div
                  className="text-xs font-bold truncate"
                  style={{ color: labelColor }}
                >
                  {name}
                </div>
              </div>
              <div
                className="shrink-0 flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {hasFeatureFlag("enableNorthbeamIndex") &&
                  NBX_SUPPORTED_METRICS.includes(tile.metricId) && (
                    <IndexCheckbox
                      title="NBX"
                      value={!!tile.showNBX}
                      onChange={(val) => {
                        setTiles(
                          tiles.map((e) =>
                            e.id === tile.id ? { ...e, showNBX: val } : e,
                          ),
                        );
                      }}
                    />
                  )}
                <div
                  className="shrink-0 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <GenericTooltip
                    noInfoCircle
                    content="Configure"
                    overlayClassName="pointer-events-none"
                    placement="top"
                    mouseEnterDelay={500}
                    onOpen={() => setIsInternalTooltipVisible(true)}
                    onClose={() => setIsInternalTooltipVisible(false)}
                  >
                    <button
                      className="rounded-md h-6 w-6 flex-center text-xs hover:bg-cosmic-3 -mr-1.5"
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="fa-regular fa-gear text-v2-secondary"></i>
                    </button>
                  </GenericTooltip>
                </div>
              </div>
            </div>
            <OverrideTags
              tile={tile}
              onTooltipOpen={() => setIsInternalTooltipVisible(true)}
              onTooltipClose={() => setIsInternalTooltipVisible(false)}
            />
            {metricData.status !== "loading" && (
              <div
                className={clsx(
                  "flex-1 flex items-center gap-2 text-v2-action tracking-wide text-xl leading-6",
                )}
              >
                <MetricValue
                  as="span"
                  format={metric.format}
                  value={value}
                  precision="exact"
                />
                {benchmarkData?.status === "loading" && (
                  <Spinner colorClassname="w-3 h-3 text-v2-action text-xxs" />
                )}
                {benchmarkIndicatorRow !== null && (
                  <UniversalBenchmarksIndicator
                    benchmarkIndicatorRows={[benchmarkIndicatorRow]}
                  />
                )}
              </div>
            )}
          </div>
          {metricData.status === "loading" ? (
            <div className="flex items-center justify-center h-full">
              <Spinner colorClassname="text-cosmic mb-5" />
            </div>
          ) : (
            <CorrelationRow
              tile={tile}
              correlation={correlation}
              onOpenTooltip={() => setIsInternalTooltipVisible(true)}
              onCloseTooltip={() => setIsInternalTooltipVisible(false)}
            />
          )}
        </div>
      </GenericTooltip>
      {isEditing && (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <TileModal
            isOpen={isEditing}
            initialName={tile.name || undefined}
            initialMetricId={tile.metricId}
            initialAttributionModel={tile.attributionModel ?? undefined}
            initialAttributionWindow={tile.attributionWindow ?? undefined}
            initialAccountingMode={tile.accountingMode ?? undefined}
            initialCompareDateRange={tile.compareDateRange ?? undefined}
            initialFilterQuery={tile.filterQuery ?? undefined}
            initialFilters={tile.filters}
            initialAdvancedSearch={tile.advancedSearch ?? undefined}
            onClose={() => {
              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onClick",
                additionalActionInfo: "Close metric settings modal",
              });

              setIsEditing(false);
            }}
            onSubmit={(value) => {
              const newTiles = tiles.map((e) =>
                e.id === tile.id
                  ? {
                      ...e,
                      name: value.name,
                      metricId: value.metricId,
                      attributionModel: value.attributionModel,
                      accountingMode: value.accountingMode,
                      attributionWindow: value.attributionWindow,
                      compareDateRange: value.compareDateRange,
                      filterQuery: value.filterQuery,
                      filters: value.filters,
                      advancedSearch: value.advancedSearch,
                    }
                  : e,
              );

              prettyLogEventPage({
                domain: "Metrics Explorer",
                action: "onChange",
                additionalActionInfo: "Update tile",
                data: {
                  newTiles,
                  pageContext: { tiles },
                },
              });

              setTiles(newTiles);
              setIsEditing(false);
            }}
            onDuplicate={() => {
              setIsEditing(false);
              onDuplicate();
            }}
            onDelete={() => {
              setIsEditing(false);
              onDelete();
            }}
          />
        </div>
      )}
    </div>
  );
};
